import { useAuth0 } from "@auth0/auth0-react"
import {
  Avatar,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  Divider,
  Stack,
  alpha,
  useTheme,
} from "@mui/material"
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined"
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined"
import { useStateContext } from "contexts/userContext"
import { useEffect, useState } from "react"
import { getRestaurant } from "helpers/api"
import { useNavigate } from "react-router-dom"
import { formatDateForView } from "helpers/date"
import { useTranslation } from "react-i18next"
import { getFreeSubscriptionEndDate } from "constants/subscription"

export const Profile = () => {
  const { settings, restaurant_id } = useStateContext()
  const { user, isAuthenticated } = useAuth0()
  const [restaurant, setRestaurant] = useState(null)
  const navigate = useNavigate()
  const { t } = useTranslation()
  const theme = useTheme()

  useEffect(() => {
    getRestaurant(restaurant_id).then((data) => {
      if (!data) return
      setRestaurant(data)
    })
  }, [restaurant_id])

  const onSubscriptionBtnClick = () => {
    navigate("/subscription")
  }

  const { subscription_start_date, is_subscription_active } = restaurant || {}

  const subscriptionStartDateStr = subscription_start_date
    ? formatDateForView(new Date(subscription_start_date))
    : null
  // Период подписки синхронизируем с глобальной длительностью FREE_SUBSCRIPTION_MONTHS:
  // дата окончания = дата начала + N месяцев (как указано на главной странице).
  const subscriptionEndDateStr = subscription_start_date
    ? formatDateForView(getFreeSubscriptionEndDate(subscription_start_date))
    : null

  const avatarSrc = settings?.logoImage ? settings.logoImage : user?.picture

  // Поля профиля для отображения карточками (исключаем технические/служебные ключи)
  const hiddenKeys = ["picture", "sub", "updated_at", "email_verified"]
  const profileEntries = Object.keys(user || {})
    .filter((key) => !hiddenKeys.includes(key) && user[key])
    .map((key) => ({ key, value: user[key] }))

  if (!isAuthenticated) return null

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: "background.default", minHeight: "100vh" }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 800,
            mb: 1,
            color: "text.primary",
            fontSize: { xs: "2rem", md: "2.5rem" },
          }}
        >
          {t("Profile")} 👤
        </Typography>
        <Typography variant="body1" sx={{ color: "text.secondary" }}>
          {t("Your account and subscription details")}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Карточка с аватаром и основной информацией */}
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              height: "100%",
              background: `linear-gradient(135deg, ${alpha(
                theme.palette.primary.main,
                0.06
              )} 0%, ${alpha(theme.palette.secondary.main, 0.06)} 100%)`,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <CardContent sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 4 }}>
              <Avatar
                src={avatarSrc}
                alt={user?.name}
                sx={{
                  width: 110,
                  height: 110,
                  mb: 2,
                  boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.25)}`,
                  border: `3px solid ${theme.palette.background.default}`,
                }}
              />
              <Typography variant="h5" sx={{ fontWeight: 700, textAlign: "center" }}>
                {user?.name}
              </Typography>
              {user?.email && (
                <Typography variant="body2" sx={{ color: "text.secondary", mb: 2, textAlign: "center" }}>
                  {user.email}
                </Typography>
              )}
              <Chip
                icon={<BadgeOutlinedIcon />}
                label={`ID: ${restaurant_id ?? "—"}`}
                variant="outlined"
                sx={{ fontWeight: 600 }}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Детали профиля */}
        <Grid item xs={12} md={8}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
                {t("Account Information")}
              </Typography>
              <Grid container spacing={2}>
                {profileEntries.map(({ key, value }) => (
                  <Grid item xs={12} sm={6} key={key}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.04),
                        border: `1px solid ${theme.palette.divider}`,
                        height: "100%",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.04em" }}
                      >
                        {t(key)}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5, wordBreak: "break-word" }}>
                        {String(value)}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Подписка */}
        {subscriptionStartDateStr && subscriptionEndDateStr && (
          <Grid item xs={12}>
            <Card
              sx={{
                background: `linear-gradient(135deg, ${alpha(
                  theme.palette.success.main,
                  0.06
                )} 0%, ${alpha(theme.palette.success.main, 0.02)} 100%)`,
                border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
              }}
            >
              <CardContent>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  justifyContent="space-between"
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "12px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: alpha(theme.palette.success.main, 0.12),
                      }}
                    >
                      <WorkspacePremiumOutlinedIcon sx={{ color: theme.palette.success.main }} />
                    </Box>
                    <Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          {t("Subscription")}
                        </Typography>
                        <Chip
                          size="small"
                          color={is_subscription_active ? "success" : "default"}
                          label={is_subscription_active ? t("Active") : t("Inactive")}
                          sx={{ fontWeight: 600 }}
                        />
                      </Box>
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        {subscriptionStartDateStr} — {subscriptionEndDateStr}
                      </Typography>
                    </Box>
                  </Box>

                  <Button variant="contained" color="primary" onClick={onSubscriptionBtnClick}>
                    {is_subscription_active ? t("Renew your subscription") : t("Buy a subscription")}
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  )
}
